import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import {
  FaEnvelope,
  FaUser,
  FaTag,
  FaCommentDots,
  FaPaperPlane,
} from "react-icons/fa";
import { GiSparkles } from "react-icons/gi";
import BottomNavigation from "../BottomNavigation";
import Footer from "../Home/Footer";
import { GoldButton } from "../../Modals/BalanceModal";

const CONTACT_EMAIL = "company@kurdbetdax200.com";
const MAX_MESSAGE_LENGTH = 1000;

const FormField = ({ icon, label, children }) => (
  <div className="mb-4">
    <span className="flex items-center gap-1.5 text-secondary-300 text-xs uppercase tracking-[0.15em] mb-2">
      {icon}
      {label}
    </span>
    {children}
  </div>
);

const Contact = () => {
  const { t } = useTranslation();

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [sending, setSending] = React.useState(false);

  const inputClass =
    "w-full rounded-xl border border-secondary-400 bg-secondary-700/40 px-3.5 py-3 text-white placeholder-secondary-300 text-sm outline-none";

  const handleSubmit = () => {
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error(t("CONTACT_FORM_ERROR_REQUIRED"));
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      toast.error(t("CONTACT_FORM_ERROR_EMAIL"));
      return;
    }

    setSending(true);

    const mailSubject = subject.trim() || t("CONTACT_FORM_SUBJECT_PLACEHOLDER");
    const mailBody = `${message.trim()}\n\n${t("CONTACT_FORM_NAME_LABEL")}: ${name.trim()}\n${t(
      "CONTACT_FORM_EMAIL_LABEL",
    )}: ${email.trim()}`;

    const mailtoLink = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      mailSubject,
    )}&body=${encodeURIComponent(mailBody)}`;

    window.location.href = mailtoLink;

    toast.success(t("CONTACT_FORM_SUCCESS"));
    setName("");
    setEmail("");
    setSubject("");
    setMessage("");
    setSending(false);
  };

  return (
    <>
      <div
        style={{
          overflow: "auto",
          height: "calc(100dvh - 80px)",
          WebkitOverflowScrolling: "touch",
          backgroundImage: `url(${require("../../../assets/img/bg.jpg")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          paddingBottom: "calc(120px + env(safe-area-inset-bottom))",
        }}
        className="bg-secondary-500"
      >
        <Helmet>
          <title>{t("TITLE_CONTACT_US")}</title>
        </Helmet>

        <div className="px-4 pt-6 max-w-2xl mx-auto space-y-4">
          {/* Hero */}
          <div className="rounded-2xl overflow-hidden bg-secondary-700/70 shadow-2xl backdrop-blur-lg">
            <div className="relative bg-gradient-to-br from-secondary-50/40 via-primary-400/40 to-secondary-50/40 px-5 py-6 overflow-hidden">
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background:
                    "conic-gradient(from 210deg at 25% 30%, transparent, rgba(255,255,255,0.35), transparent 35%)",
                }}
              />
              <div className="absolute -end-8 -top-8 w-40 h-40 rounded-full bg-primary-400/25 blur-3xl" />
              <div className="absolute -start-10 bottom-0 w-32 h-32 rounded-full bg-primary-400/25 blur-3xl" />

              <div className="relative flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  <GiSparkles className="absolute -top-2 -start-2 text-primary-300 text-lg" />
                  <div className="w-14 h-14 rounded-full border-4 border-primary-400 bg-secondary-900/70 shadow-lg flex items-center justify-center">
                    <FaEnvelope className="text-primary-400 text-xl" />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-white font-extrabold text-xl break-words">
                    {t("CONTACT_HERO_TITLE")}
                  </div>
                  <div className="text-white/60 text-xs mt-0.5 break-words">
                    {t("CONTACT_HERO_SUBTITLE")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Direct email info */}
          <div className="rounded-2xl overflow-hidden bg-secondary-700/70 shadow-2xl backdrop-blur-sm">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="px-5 py-5 flex items-center gap-4"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-400/15 border border-primary-400/40 text-primary-400 text-xl flex items-center justify-center">
                <FaEnvelope />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white/60 text-xs">
                  {t("CONTACT_INFO_EMAIL_LABEL")}
                </div>
                <div className="text-primary-400 font-bold text-base break-all">
                  {CONTACT_EMAIL}
                </div>
              </div>
            </a>
          </div>

          {/* Form */}
          <div className="rounded-2xl overflow-hidden bg-secondary-700/70 shadow-2xl backdrop-blur-sm">
            <div className="px-5 py-5">
              <FormField
                icon={<FaUser className="text-primary-400" />}
                label={t("CONTACT_FORM_NAME_LABEL")}
              >
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("CONTACT_FORM_NAME_PLACEHOLDER")}
                  maxLength={100}
                  className={inputClass}
                />
              </FormField>

              <FormField
                icon={<FaEnvelope className="text-primary-400" />}
                label={t("CONTACT_FORM_EMAIL_LABEL")}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("CONTACT_FORM_EMAIL_PLACEHOLDER")}
                  maxLength={150}
                  className={inputClass}
                />
              </FormField>

              <FormField
                icon={<FaTag className="text-primary-400" />}
                label={t("CONTACT_FORM_SUBJECT_LABEL")}
              >
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t("CONTACT_FORM_SUBJECT_PLACEHOLDER")}
                  maxLength={150}
                  className={inputClass}
                />
              </FormField>

              <FormField
                icon={<FaCommentDots className="text-primary-400" />}
                label={t("CONTACT_FORM_MESSAGE_LABEL")}
              >
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t("CONTACT_FORM_MESSAGE_PLACEHOLDER")}
                  rows={4}
                  maxLength={MAX_MESSAGE_LENGTH}
                  className={`${inputClass} resize-none`}
                />
              </FormField>

              <GoldButton
                label={t("CONTACT_FORM_SUBMIT")}
                onClick={handleSubmit}
                disabled={sending}
                loading={sending}
                icon={<FaPaperPlane className="w-4 h-4" />}
              />
            </div>
          </div>
        </div>

        <Footer />
      </div>

      <BottomNavigation isNavVisible={true} />
    </>
  );
};

export default Contact;
